import { Account, AccountType, LifecycleTemplate, ThingLifeCycleStateResponse, ThingLifeCycleStateResponseStakeholder, WithAuthoritative } from '@eagle/core-data-types';
import { CardContent, Stack, styled } from '@ethings/ui-toolkit';
import { Paper } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { evaluate } from '../../evaluator';
import { useFetchAllCache, usePromise, useUiTemplate } from '../../hooks';
import { CacheDataTypes, Nullable } from '../../types';
import { LOAN_MANAGEMENT_CARD } from '../../ui-templates/loan-management';
import { getMaybePropertyValue } from '../format';
import { LoanStatusColor } from '../loan-status-chip/loan-status-chip';
import { ResponsiveText } from '../responsive-text';

const EXPIRING_SOON_DAYS = 14;

interface Props {
  lifecycleState: ThingLifeCycleStateResponse | null;
}

interface LoanProperties {
  startDate?: WithAuthoritative;
  finishDate?: WithAuthoritative;
  [key: string]: WithAuthoritative | undefined;
}

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  paddingBottom: theme.spacing(1.5),
}));

const STAGE_IDS = {
  LEASED: 'leased',
  DEMO: 'on-demo',
  RENT: 'on-rent',
} as const;

export const LoanManagementCard: FC<Props> = ({ lifecycleState }): ReactElement | null => {
  const { t } = useTranslation('admin');
  const { restClient, userInfo } = useAuthenticated();
  const accountCache = useFetchAllCache(CacheDataTypes.ACCOUNT);
  const accountTypeCache = useFetchAllCache(CacheDataTypes.ACCOUNT_TYPE);
  const { template: loanCardTemplate, loaded: cardLoaded } = useUiTemplate('loan-management-row', LOAN_MANAGEMENT_CARD);

  const titleText = t('admin:page.thing-details.lease-management.labels');
  const messageText = t('admin:page.thing-details.lease-management.no-active-lease.labels');

  const { isLessorView, isCustomerView, isDealerView } = useMemo(() => {
    const isCustomerStakeholder = lifecycleState?.stakeholders?.customer?._id === userInfo.accountId || false;
    const isLessorStakeholder = lifecycleState?.stakeholders?.lessor?._id === userInfo.accountId || false;
    const isDealerStakeholder = lifecycleState?.stakeholders?.dealer?._id === userInfo.accountId || false;

    return {
      isCustomerView: isCustomerStakeholder,
      isLessorView: isLessorStakeholder,
      isDealerView: isDealerStakeholder,
    };
  }, [lifecycleState, userInfo.accountId]);

  const [lifecycleTemplate, lifecycleTemplateError] = usePromise<LifecycleTemplate | null>(
    async () => {
      if (!lifecycleState?.lifecycleTemplateId) return null;
      return restClient.lifecycleTemplate.get(lifecycleState.lifecycleTemplateId);
    },
    [lifecycleState?.lifecycleTemplateId, restClient],
  );

  const currentStage = useMemo(() => {
    if (!lifecycleTemplate || !lifecycleState?.stageId) return null;
    return lifecycleTemplate.stages.find((stage) => stage.stageId === lifecycleState.stageId);
  }, [lifecycleTemplate, lifecycleState?.stageId]);

  const [stakeholdersData, stakeholdersDataError] = usePromise(async () => {
    const stakeholderEntries: [string, Nullable<ThingLifeCycleStateResponseStakeholder>][] = Object.entries(lifecycleState?.stakeholders || {});
    const resolvedStakeholders: Record<string, { display?: string; accountTypeDisplay?: string }> = {};

    await Promise.all(
      stakeholderEntries.map(async ([key, stakeholder]) => {
        if (!stakeholder?._id) return;
        const account = await accountCache.one<Account>(stakeholder._id);
        const accountType = await accountTypeCache.one<AccountType>(account?.accountTypeId);
        resolvedStakeholders[key] = {
          display: account?.display || stakeholder.display,
          accountTypeDisplay: accountType?.display || stakeholder?.accountType?.display,
        };
      }),
    );
    return resolvedStakeholders;
  }, [lifecycleState, accountCache, accountTypeCache]);

  const hasLoanAddOn = useMemo(() => {
    return Boolean(currentStage?.addOns?.loan);
  }, [currentStage]);

  const loanDates = useMemo(() => {
    if (!lifecycleState?.stageProperties) {
      return { startDate: null, finishDate: null };
    }
    const stageProps = lifecycleState.stageProperties.loan as unknown as LoanProperties;
    if (!stageProps) {
      return { startDate: null, finishDate: null };
    }
    return {
      startDate: getMaybePropertyValue<string>(stageProps.startDate),
      finishDate: getMaybePropertyValue<string>(stageProps.finishDate),
    };
  }, [lifecycleState?.stageProperties]);

  const statusDetails = useMemo(() => {
    const { startDate, finishDate } = loanDates;
    const leaseStatus = (startDate && finishDate) ? getLeaseStatus(startDate, finishDate) : undefined;

    if (!leaseStatus) {
      return null;
    }

    switch (leaseStatus.status) {
      case 'ACTIVE':
        return {
          statusDisplay: t('admin:page.thing-details.loan-management.active.labels'),
          statusColor: 'success' as LoanStatusColor,
        };
      case 'EXPIRED':
        return {
          statusDisplay: leaseStatus.daysExpired > 0 ? t('admin:page.thing-details.loan-management.expired-days-ago.labels', { count: leaseStatus.daysExpired }) : t('admin:page.thing-details.loan-management.expired.labels'),
          statusColor: 'error' as LoanStatusColor,
        };
      case 'EXPIRING_SOON':
        return {
          statusDisplay: t('admin:page.thing-details.loan-management.expiring-soon.labels'),
          statusColor: 'warning' as LoanStatusColor,
        };
      case 'NOT_STARTED':
        return {
          statusDisplay: t('admin:page.thing-details.loan-management.not-started.labels'),
          statusColor: 'default' as LoanStatusColor,
        };
    }
  }, [loanDates, t]);

  const hasLeaseData = Boolean(loanDates.startDate && loanDates.finishDate);
  const currentStageId = currentStage?.stageId;
  const isDualLessorDealerView = isLessorView && isDealerView;

  const hideCard = useMemo(() => {
    return (currentStageId !== STAGE_IDS.DEMO && isDealerView && !isDualLessorDealerView) || (currentStageId === STAGE_IDS.DEMO && isLessorView && !isDualLessorDealerView);
  }, [currentStageId, isLessorView, isDealerView, isDualLessorDealerView]);

  const context = useMemo(() => ({
    loanDates,
    statusDetails,
    stakeholdersData,
    showLessor: currentStageId !== STAGE_IDS.DEMO && !isLessorView,
    showCustomer: !isCustomerView,
    showDealer: currentStageId === STAGE_IDS.DEMO && !isDealerView,
  }), [stakeholdersData, loanDates, statusDetails, currentStageId, isCustomerView, isDealerView, isLessorView]);

  if (!cardLoaded || stakeholdersDataError || lifecycleTemplateError || !hasLoanAddOn || hideCard) {
    return null;
  }

  return (
    <Paper>
      <StyledCardContent data-testid="loan-management-card">
        <Stack direction="row" justifyContent="space-between" mb={2}>
          <ResponsiveText
            variant="h5"
            color="text.primary"
            data-testid="loan-management-title"
            responsiveOptions={{
              minFontSize: '1.25rem',
              maxFontSize: '1.5rem',
              textLengthThreshold: 25,
            }}
          >
            {titleText}
          </ResponsiveText>
        </Stack>
        {hasLeaseData ? (
          <>{evaluate(loanCardTemplate, context)}</>
        ) : (
          <ResponsiveText
            variant="body2"
            data-testid="loan-management-no-active-loan"
            responsiveOptions={{
              minFontSize: '0.75rem',
              maxFontSize: '0.875rem',
              textLengthThreshold: 50,
            }}
          >
            {messageText}
          </ResponsiveText>
        )}
      </StyledCardContent>
    </Paper>
  );
};

type LeaseStatus =
  | { status: 'NOT_STARTED' }
  | { status: 'ACTIVE' }
  | { status: 'EXPIRING_SOON' }
  | { status: 'EXPIRED'; daysExpired: number }

const getLeaseStatus = (
  startDate: string,
  finishDate: string,
): LeaseStatus => {
  const now = DateTime.now();
  const startDateTime = DateTime.fromISO(startDate);
  const finishDateTime = DateTime.fromISO(finishDate);
  const daysToFinish = finishDateTime.diff(now, 'days').as('days');
  const daysSinceFinish = now.diff(finishDateTime, 'days').as('days');

  if (now < startDateTime) {
    return { status: 'NOT_STARTED' };
  }

  if (now > finishDateTime) {
    return { status: 'EXPIRED', daysExpired: Math.floor(daysSinceFinish) };
  }

  if (daysToFinish <= EXPIRING_SOON_DAYS) {
    return { status: 'EXPIRING_SOON' };
  }

  return { status: 'ACTIVE' };
};
